import { Fade, Grid, SnackbarContent, makeStyles } from "@material-ui/core";
import green from "@material-ui/core/colors/green";
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from "@material-ui/icons";
import * as React from "react";
const useStyles = makeStyles((theme: any) => ({
  container: {
    margin: "0 0 32px"
  },
  error: {
    background: theme.palette.error.dark,
    "& ul": {
      margin: "0",
      padding: "0",
      listStyle: "none"
    }
  },
  icon: {
    marginRight: "12px"
  },
  info: {
    background: "white",
    borderLeft: "8px solid #ed9751",
    color: "black"
  },
  snackbar: {
    fontSize: "1.6rem",
    minWidth: "100%"
  },
  success: {
    background: green[600]
  }
}));

export interface IOwnProps {
  onClose?: () => void;
  children?: React.ReactNode;
  dataTestId?: string;
  fadeDuration?: number;
  duration?: number;
  type: "success" | "error" | "info";
  onScrollFinish?: () => void;
}

const FadeMessage = ({
  children,
  onClose,
  fadeDuration = 200,
  dataTestId,
  duration = 5000,
  type,
  onScrollFinish
}: IOwnProps) => {
  const classes = useStyles();
  const containerRef = React.createRef<HTMLDivElement>();
  const [fadeing, setFade] = React.useState(true);
  let fadeTimeout: NodeJS.Timer;
  let unmountTimeout: NodeJS.Timer;
  let scrollTimeout: NodeJS.Timer;

  React.useEffect(() => {
    if (!containerRef.current || !onScrollFinish) {
      return;
    }

    const intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        const thisEntry = entries.find(entry => entry.target === containerRef.current);
        if (thisEntry && thisEntry.isIntersecting) {
          onScrollFinish();
        }
      },
      {
        threshold: 1
      }
    );
    intersectionObserver.observe(containerRef.current);

    return () => {
      intersectionObserver.disconnect();
    };
  }, [containerRef.current, onScrollFinish]);

  React.useEffect(() => {
    clearTimeout(scrollTimeout);

    scrollTimeout = setTimeout(() => {
      if (containerRef.current) {
        const bodyRect = document.body.getBoundingClientRect();
        const elemRect = containerRef.current.getBoundingClientRect();
        const offset = elemRect.top - bodyRect.top;

        window.scrollTo({
          behavior: "smooth",
          top: offset - 90 // Header offset
        });
      }
    }, 100);

    if (duration > 0) {
      fadeTimeout = setTimeout(() => {
        setFade(false);
        if (onClose) {
          unmountTimeout = setTimeout(() => {
            onClose();
          }, fadeDuration);
        }
      }, duration);
    }

    return () => {
      if (duration > 0) {
        clearTimeout(fadeTimeout);
      }
      clearTimeout(unmountTimeout);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const timeout = {
    exit: fadeDuration
  };

  const snackBarClasses = [classes.snackbar];

  if (type === "error") {
    snackBarClasses.push(classes.error);
  } else if (type === "success") {
    snackBarClasses.push(classes.success);
  } else if (type === "info") {
    snackBarClasses.push(classes.info);
  }

  return (
    <Fade timeout={timeout} in={fadeing}>
      <div ref={containerRef} className={classes.container}>
        <SnackbarContent
          data-qa-selector={dataTestId}
          classes={{ root: snackBarClasses.join(" ") }}
          message={
            <Grid container wrap="nowrap" alignItems="center">
              {type === "error" && <ErrorIcon className={classes.icon} />}
              {type === "success" && <CheckCircleIcon className={classes.icon} />}
              {children}
            </Grid>
          }
        />
      </div>
    </Fade>
  );
};

export default React.memo(FadeMessage);
